import { useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { integerToCurrency } from '@actual-app/core/shared/util';

import { FinancialText } from '#components/FinancialText';

import { JevCategoryPicker } from './JevCategoryPicker';

type CategoryLabel = {
  categoryId: string;
  categoryName: string;
  groupName: string;
};

export type JevTransaction = {
  transactionId: string;
  description: string;
  payeeName: string | null;
  notes: string | null;
  amount: number;
  date: string;
  accountName: string;
  history: (CategoryLabel & { count: number })[];
};

export type JevSuggestion = JevTransaction &
  CategoryLabel & {
    confidence: number;
    alternatives: (CategoryLabel & { probability: number })[];
  };

type JevSuggestionCardProps = {
  // Without a suggestion the card only offers earlier categories and the
  // category picker.
  transaction: JevTransaction | JevSuggestion;
  onAccept: (categoryId: string) => void;
  onDismiss?: () => void;
};

export function JevSuggestionCard({
  transaction,
  onAccept,
  onDismiss,
}: JevSuggestionCardProps) {
  const suggestion: JevSuggestion | null =
    'categoryId' in transaction ? transaction : null;
  const { t } = useTranslation();
  const [isPicking, setIsPicking] = useState(false);

  return (
    <View
      style={{
        backgroundColor: theme.tableBackground,
        border: '1px solid ' + theme.tableBorder,
        borderRadius: 4,
        padding: 12,
        gap: 10,
        flexShrink: 0,
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: 20,
          flexShrink: 0,
        }}
      >
        <View style={{ gap: 2, flex: 1 }}>
          {transaction.description ? (
            <Text style={{ fontWeight: 600 }}>{transaction.description}</Text>
          ) : (
            <Text style={{ fontWeight: 600, color: theme.pageTextSubdued }}>
              <Trans>No description from the bank</Trans>
            </Text>
          )}
          <Text style={{ color: theme.pageTextSubdued }}>
            {transaction.date} · {transaction.accountName}
          </Text>
        </View>
        <FinancialText style={{ fontWeight: 600 }}>
          {integerToCurrency(transaction.amount)}
        </FinancialText>
      </View>

      {(transaction.payeeName || transaction.notes) && (
        <View style={{ gap: 2, flexShrink: 0 }}>
          {transaction.payeeName && (
            <Detail label={t('Payee')}>{transaction.payeeName}</Detail>
          )}
          {transaction.notes && (
            <Detail label={t('Notes')}>{transaction.notes}</Detail>
          )}
        </View>
      )}

      <View style={{ gap: 6, flexShrink: 0 }}>
        {suggestion && (
          <Text>
            {t('Suggested category: {{name}} ({{percent}}% sure)', {
              name: categoryLabel(suggestion),
              percent: Math.round(suggestion.confidence * 100),
            })}
          </Text>
        )}
        {suggestion && suggestion.alternatives.length > 0 && (
          <Options label={t('Other options')}>
            {suggestion.alternatives.map(option => (
              <OptionButton
                key={option.categoryId}
                onPress={() => onAccept(option.categoryId)}
              >
                {t('{{name}} ({{percent}}%)', {
                  name: categoryLabel(option),
                  percent: Math.round(option.probability * 100),
                })}
              </OptionButton>
            ))}
          </Options>
        )}
        {transaction.history.length > 0 ? (
          <Options label={t('Same description before')}>
            {transaction.history.map(option => (
              <OptionButton
                key={option.categoryId}
                onPress={() => onAccept(option.categoryId)}
              >
                {t('{{name}} ({{count}}×)', {
                  name: categoryLabel(option),
                  count: option.count,
                })}
              </OptionButton>
            ))}
          </Options>
        ) : (
          <Text style={{ color: theme.pageTextSubdued, fontSize: 12 }}>
            <Trans>No earlier transaction with this description.</Trans>
          </Text>
        )}
      </View>

      {isPicking ? (
        <JevCategoryPicker
          onSelect={onAccept}
          onCancel={() => setIsPicking(false)}
        />
      ) : (
        <View style={{ flexDirection: 'row', gap: 10, flexShrink: 0 }}>
          {suggestion && (
            <Button
              variant="primary"
              onPress={() => onAccept(suggestion.categoryId)}
            >
              <Trans>Accept</Trans>
            </Button>
          )}
          <Button
            variant={suggestion ? 'normal' : 'primary'}
            onPress={() => setIsPicking(true)}
          >
            {suggestion ? (
              <Trans>Choose another category</Trans>
            ) : (
              <Trans>Choose category</Trans>
            )}
          </Button>
          {onDismiss && (
            <Button variant="bare" onPress={onDismiss}>
              <Trans>Dismiss</Trans>
            </Button>
          )}
        </View>
      )}
    </View>
  );
}

function categoryLabel({ categoryName, groupName }: CategoryLabel) {
  return groupName ? `${groupName} › ${categoryName}` : categoryName;
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Text>
      <Text style={{ color: theme.pageTextSubdued }}>{label}: </Text>
      {children}
    </Text>
  );
}

function Options({ label, children }: { label: string; children: ReactNode }) {
  return (
    <View
      style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 6,
        flexShrink: 0,
      }}
    >
      <Text style={{ color: theme.pageTextSubdued, fontSize: 12 }}>
        {label}:
      </Text>
      {children}
    </View>
  );
}

function OptionButton({
  onPress,
  children,
}: {
  onPress: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      variant="bare"
      onPress={onPress}
      style={{
        fontSize: 12,
        padding: '2px 8px',
        border: '1px solid ' + theme.tableBorder,
        borderRadius: 12,
      }}
    >
      {children}
    </Button>
  );
}
